/** Общие кусочки форм договора: поле с подписью, классы ввода, итог записи в CRM. */
import { useEffect, useRef } from "react";

import type { ContractWriteResult } from "@/lib/contract";

const ring =
  "rounded-[var(--radius-ui)] border bg-transparent px-2 hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent";
/** Поле во всю ширину ячейки; цвет рамки (`border-border` / `border-err`) — у вызывающего. */
export const inputCls = `h-8 w-full ${ring}`;
/** Короткое числовое поле (ширину задаёт вызывающий). */
export const numCls = `h-8 border-border tabular-nums ${ring}`;

export function Field({ label, error, children, wide = false }: { label: string; error?: string | undefined; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`flex flex-col gap-0.5 ${wide ? "w-full" : "min-w-[9rem] flex-1"}`}>
      <span className="text-fg-muted">{label}</span>
      {children}
      {error && <span className="text-[11.5px] text-err">{error}</span>}
    </div>
  );
}

/**
 * Итог записи: «✓ …», предупреждения ответа (пересечения с бронями) и сбой синхронизации с Bitrix.
 * Есть `onClose`: чистый успех сам скрывается через 4 с, с предупреждением — висит до «скрыть».
 */
export function WriteOutcome({ r, ok, onClose }: { r: ContractWriteResult; ok: string; onClose?: (() => void) | undefined }) {
  const syncErr = r.sync.status === "error";
  const clean = !syncErr && r.warnings.length === 0;
  // Ссылка, а не зависимость: новая функция на каждой перерисовке не должна сбрасывать таймер.
  const close = useRef(onClose);
  close.current = onClose;
  const auto = Boolean(onClose) && clean;
  useEffect(() => {
    if (!auto) return;
    const t = window.setTimeout(() => close.current?.(), 4000);
    return () => window.clearTimeout(t);
  }, [auto, r]);
  return (
    <div className="flex flex-col gap-0.5 text-[12px]" data-annot="итог записи договора">
      <div className="text-ok">✓ {ok}</div>
      {r.sync.status === "ok" && r.sync.detail?.includes("сверено") && <div className="break-words text-ok">✓ Bitrix: {r.sync.detail}</div>}
      {syncErr && <div className="break-words text-err">Синхронизация с Bitrix: {r.sync.detail ?? "ошибка"}</div>}
      {r.warnings.map((w, i) => (
        <div key={i} className="font-medium text-err">
          {w}
        </div>
      ))}
      {onClose && !clean && (
        <button type="button" onClick={onClose} className="self-start text-accent-text hover:underline">
          скрыть
        </button>
      )}
    </div>
  );
}
